import {
  Image,
  StyleSheet,
  Text,
  View,
  Document,
  Font,
} from '@react-pdf/renderer';
import type { ComponentProps } from 'react';
import { StatementPageContainer } from '../../../statements/legacy/pdf/components/StatementPageContainer';
import { StatementTable } from '../../../statements/legacy/pdf/components/StatementTable';
import { TableTitle } from '../../../statements/legacy/pdf/components/TableTitle';
import type { OwnerStatementSummaryPdfModel } from './OwnerStatementSummaryPdf.utils';

let fontsRegistered = false;

const ensureFonts = () => {
  if (fontsRegistered) {
    return;
  }

  Font.register({
    family: 'Helvetica-Bold',
    fonts: [],
  });

  fontsRegistered = true;
};

const styles = StyleSheet.create({
  cards: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'nowrap',
    paddingHorizontal: 5,
    marginBottom: 20,
    marginTop: -10,
    gap: 10,
  },
  infoCard: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 250,
    padding: 9,
    border: '1px solid rgb(226,229,232)',
    borderRadius: 5,
    flexGrow: 1,
  },
  summaryCard: {
    width: 320,
    padding: 14,
    border: '1px solid rgb(226,229,232)',
    borderRadius: 5,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  metaText: {
    fontSize: 8,
    color: '#707786',
  },
  logo: {
    width: 30,
    height: 30,
    marginRight: 10,
    padding: 3,
    objectFit: 'contain',
    border: '1px solid rgb(233,236,239)',
    borderRadius: 5,
  },
  section: {
    marginTop: 20,
  },
  summaryLabel: {
    fontSize: 8,
    color: '#707786',
    marginBottom: 4,
  },
  summaryTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
  },
  divider: {
    borderTop: '1px solid #E5E7EB',
    marginBottom: 8,
  },
  summaryLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 4,
  },
  summaryLineText: {
    fontSize: 9,
    color: '#374151',
  },
  fieldRow: {
    marginBottom: 8,
    fontSize: 8,
    paddingBottom: 5,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  fieldLabel: {
    color: '#5C6178',
    width: 50,
  },
  teamHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  teamLabel: {
    fontSize: 8,
    color: '#707786',
    marginBottom: 2,
  },
  teamName: {
    fontSize: 12,
  },
  listingName: {
    fontSize: 8,
    marginBottom: 5,
  },
  listingAddress: {
    fontSize: 10,
    marginBottom: 15,
  },
});

const isEmphasizedSummaryLine = (lineName: string, index: number) => {
  if (index === 0) {
    return true;
  }

  return [
    'balance start',
    'balance forward',
    'balance end',
    'available balance',
  ].includes(lineName.toLowerCase());
};

export const OwnerStatementSummaryPdfDocument = ({
  model,
}: {
  model: OwnerStatementSummaryPdfModel;
}) => {
  ensureFonts();

  return (
    <Document>
      <StatementPageContainer
        footer=""
        month={model.monthLabel}
        subline={model.subline}
        teamLogo={model.teamLogo || undefined}
        teamName={model.teamName}
      >
        <View style={styles.cards}>
          <InfoCard model={model} />
          <SummaryCard model={model} />
        </View>

        {model.sections.map((section) => (
          <View
            key={`${section.title}-${section.columns.length}`}
            style={styles.section}
          >
            <TableTitle>{section.title}</TableTitle>
            <StatementTable columns={section.columns} rowData={section.rows} />
          </View>
        ))}
      </StatementPageContainer>
    </Document>
  );
};

const InfoCard = ({ model }: { model: OwnerStatementSummaryPdfModel }) => {
  return (
    <View style={styles.infoCard}>
      <View style={styles.teamHeader}>
        {model.teamLogo ? (
          <Image src={model.teamLogo} style={styles.logo} />
        ) : null}
        <View>
          <Text style={styles.teamLabel}>Property Management</Text>
          <Text style={styles.teamName}>{model.teamName}</Text>
        </View>
      </View>

      {model.info.teamFields.map((field) => (
        <FieldRow
          key={`team-${field.label}`}
          label={field.label}
          value={field.value}
        />
      ))}

      {model.info.listingName ? (
        <Text style={styles.listingName}>{model.info.listingName}</Text>
      ) : null}
      {model.info.listingAddress ? (
        <Text style={styles.listingAddress}>{model.info.listingAddress}</Text>
      ) : null}

      {model.info.ownerFields.map((field) => (
        <FieldRow
          key={`owner-${field.label}`}
          label={field.label}
          value={field.value}
        />
      ))}
    </View>
  );
};

const SummaryCard = ({ model }: { model: OwnerStatementSummaryPdfModel }) => {
  return (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryLabel}>Statement Summary</Text>
      <Text style={styles.summaryTitle}>{model.summary.title}</Text>
      <View style={styles.metaRow}>
        <Text style={styles.metaText}>Layout: {model.summary.layoutName}</Text>
        <Text style={styles.metaText}>View: {model.summary.viewLabel}</Text>
      </View>
      <View style={styles.divider} />

      {model.summary.lines.map((line, index) => {
        const emphasized = isEmphasizedSummaryLine(line.name, index);
        const textStyle = emphasized
          ? { ...styles.summaryLineText, fontFamily: 'Helvetica-Bold' }
          : styles.summaryLineText;

        return (
          <View key={`${line.name}-${index}`}>
            {line.props?.separated ? <View style={styles.divider} /> : null}
            <View style={styles.summaryLine}>
              <Text style={textStyle}>{line.name}</Text>
              <Text style={textStyle}>{line.text}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const FieldRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text>{value}</Text>
    </View>
  );
};

export type OwnerStatementSummaryPdfDocumentProps = ComponentProps<
  typeof OwnerStatementSummaryPdfDocument
>;
